import React from 'react';
import { FieldOption, FieldType } from 'types/FieldTypes';
import { languageOptions } from 'internationalization/i18n';
import FormModal from 'components/modals/FormModal';
import ContactForm from '../contacts/ContactForm';

// Rename the name of the function:
const EnvironmentForm: React.FC<{ viewKey?: string }> = ({ viewKey }) => {

    const apiObject = 'environment_as_superuser'

    // Function to convert hardcoded options into dropdown options
    function convertOptionsToDropdownOptions(options: Record<string, { label: string }>): FieldOption[] {
        return Object.entries(options).map(([value, { label }]) => ({
            value,
            name: label,
        }))
    };
    
    // Convert language and locale options into dropdown options
    const languageDropdownOptions: FieldOption[] = convertOptionsToDropdownOptions(languageOptions);

    // Plug in the fields and their properties:
    const fields: FieldType[] = [
        {
            name: 'name',
            label: 'environment.general.environment_name_label',
            type: 'text',
            placeholder: 'environment.general.environment_name_placeholder',
        },
        {
            name: 'type',
            label: 'environment.general.type_label',
            type: 'dropdown',
            disabled_selected: 'forms.select_type_disabled_selected',
            showSearch: false,
            allowNoneOption: false,
            hardcodedOptions: [
                { value: 'trial', name: 'environment.type.trial'},
                { value: 'beta', name: 'environment.type.beta'},
                { value: 'api-partner', name: 'environment.type.api_partner'},
                { value: 'client', name: 'environment.type.client'},
            ],
        },
        {
            name: 'package',
            backendField: 'key',
            label: 'environment.general.package_label',
            type: 'dropdown',
            disabled_selected: 'forms.select_type_disabled_selected',
            showSearch: false,
            allowNoneOption: false,
            apiRequest: [{
                endpoint: 'get_subscription_package_list',
                object: 'subscription_package',
                params: { is_active: true, include_view_only: true },
                useCache: true
            }],
        },
        {
            type: 'multiselect',
            name: 'additional_modules',
            label: 'billing.general.additional_modules_label',
            apiObject: 'module_feature',
            disabled_selected: 'forms.select_type_disabled_selected',
            allowNoneOption: false,
            optionFormat: (option) => option.feature_translation,
            selectionFormat: 'feature_translation',
        },
        {
            type: 'dropdown',
            name: 'language',
            label: 'environment.general.language_label',
            disabled_selected: 'environment.general.language_disabled_selected',
            allowNoneOption: false,
            showSearch: false,
            hardcodedOptions: languageDropdownOptions as { value: string; name: string }[],
            defaultValue: { value: 'nl-NL' },
            showOnEdit: false,
        },
        {
            name: 'status',
            label: 'environment.general.status_label',
            type: 'dropdown',
            showOnEdit: true,
            disabled_selected: 'forms.select_type_disabled_selected',
            showSearch: false,
            allowNoneOption: false,
            hardcodedOptions: [
                { value: 'active', name: 'environment.status.active'},
                { value: 'suspended', name: 'environment.status.suspended'},
                { value: 'cancelled', name: 'environment.status.cancelled'},
            ],
        },
        {
            type: 'searchselect',
            name: 'admin_contact',
            label: 'environment.general.admin_contact_label',
            placeholder: 'environment.general.admin_contact_placeholder',
            tooltipText: 'environment.general.admin_contact_tooltip',
            objectName: 'organization.general.object_name.singular',
            query: {
                // Only get the organization contacts of the admin environment
                endpoint: 'get_contact_search',
                params: {
                    contact_type: ['contact', 'lead', 'client'],
                    entity_type: 'organization'
                }
            },
            selectionFormat: 'name',
            optionFormat: {
                title: { field: 'name', format: (value) => value },
                subtitle: { field: 'primary_location', format: (value) => value.address, optional: true },
            },
            addNewItem: { method: 'modal-form', modalForm: <ContactForm /> }
        },
        {
            type: 'header',
            header: 'environment.general.trial_settings_header',
            dependentOn: { fieldName: 'type', value: 'trial' },
            showOnEdit: false,
        },
        {
            name: 'company_size',
            label: 'environment.general.company_size_label',
            type: 'dropdown',
            disabled_selected: 'trial.general.select_company_size_disabled_selected',
            showSearch: false,
            allowNoneOption: false,
            hardcodedOptions: [
                { value: '1', name: '1' },
                { value: '2-5', name: '2-5' },
                { value: '6-15', name: '6-15'},
                { value: '16-25', name: '16-25' },
                { value: '26-50', name: '26-50' },
                { value: '51+', name: '51+' },
            ],
            dependentOn: { fieldName: 'type', value: 'trial' },
            showOnEdit: false,
        },
        {
            name: 'industry',
            label: 'environment.general.industry_label',
            type: 'dropdown',
            disabled_selected: 'forms.select_type_disabled_selected',
            showSearch: false,
            allowNoneOption: false,
            hardcodedOptions: [
                { value: 'installation_plumbing', name: 'trial.industry.installation_plumbing' },
                { value: 'security_systems', name: 'trial.industry.security_systems' },
                { value: 'blinds', name: 'trial.industry.blinds'},
                { value: 'interior', name: 'trial.industry.interior' },
                { value: 'construction', name: 'trial.industry.construction' },
                { value: 'equipment_machinery', name: 'trial.industry.equipment_machinery' },
                { value: 'signage', name: 'trial.industry.signage' },
                { value: 'landscaping', name: 'trial.industry.landscaping' },
                { value: 'facility_management', name: 'trial.industry.facility_management' },
                { value: 'other', name: 'trial.industry.other' },
            ],
            dependentOn: { fieldName: 'type', value: 'trial' },
            showOnEdit: false,
        },
        {
            type: 'header',
            header: 'environment.general.main_user_header',
            showOnEdit: false,
        },
        {
            name: 'email',
            label: 'user.general.email_label',
            type: 'email',
            placeholder: 'user.general.email_placeholder',
            showOnEdit: false,
        },
        {
            name: 'first_name',
            label: 'user.general.first_name_label',
            type: 'text',
            placeholder: 'user.general.first_name_placeholder',
            width: '50%',
            group: 'field-row',
            showOnEdit: false,
        },
        {
            name: 'last_name',
            label: 'user.general.last_name_label',
            type: 'text',
            placeholder: 'user.general.last_name_placeholder',
            width: '50%',
            group: 'field-row',
            showOnEdit: false,
        },
        {
            name: 'password',
            label: 'user.general.password_label',
            type: 'password',
            placeholder: 'user.general.password_placeholder',
            showOnEdit: false,
        },
    ];

    return (
        <FormModal
            viewKey={viewKey}
            apiObject={apiObject}
            fields={fields}
        />
    );
};

export default EnvironmentForm;